'use strict';

import { trimString, parsePriceNumber } from '../core/bambuserFormatters';
import { getColorCodeFromPid, isColorGroupPid } from './bambuserPidResolver';

// ---------------------------------------------------------------------------
// Basic product fields
// ---------------------------------------------------------------------------

export function getProductName (product) {
    return (
        trimString(product && product.productName) ||
        trimString(product && product.productDisplayName) ||
        trimString(product && product.name) ||
        trimString(product && product.id) ||
        'Product'
    );
}

export function getProductBrand (product) {
    return (
        trimString(product && product.brand) ||
        trimString(product && product.brandName) ||
        null
    );
}

export function getProductDescription (product) {
    return (
        trimString(product && product.shortDescription) ||
        trimString(product && product.description) ||
        null
    );
}

export function getProductLongDescription (product) {
    return (
        trimString(product && product.description) ||
        trimString(product && product.longDescription) ||
        null
    );
}

// ---------------------------------------------------------------------------
// Price
// ---------------------------------------------------------------------------

export function getProductPriceData (product) {
    var price = product && product.price ? product.price : null;
    if (!price) {
        return { current: null, original: null };
    }

    var salesValue = parsePriceNumber(price.sales && price.sales.value);
    var listValue = parsePriceNumber(price.list && price.list.value);
    var fallbackValue =
        parsePriceNumber(price.value) ||
        parsePriceNumber(product && product.c_price);

    var current = salesValue || listValue || fallbackValue;
    var original =
        salesValue && listValue && listValue > salesValue ? listValue : null;

    return { current: current, original: original };
}

export function isBuyablePrice (value) {
    return typeof value === 'number' && isFinite(value) && value > 0;
}

// ---------------------------------------------------------------------------
// Images
// ---------------------------------------------------------------------------

export function getProductImageUrls (product) {
    var imageUrls = [];
    var imageGroups = product && product.images ? product.images : null;

    if (!imageGroups || typeof imageGroups !== 'object') {
        return imageUrls;
    }

    // Use large images (full-size display in Bambuser overlay), fall back to small.
    var bucket =
        Array.isArray(imageGroups.large) && imageGroups.large.length
            ? imageGroups.large
            : Array.isArray(imageGroups.small)
                ? imageGroups.small
                : [];

    for (var i = 0; i < bucket.length; i++) {
        var imageUrl =
            bucket[i] && bucket[i].url ? trimString(bucket[i].url) : null;
        if (imageUrl && imageUrls.indexOf(imageUrl) === -1) {
            imageUrls.push(imageUrl);
        }
    }

    return imageUrls;
}

// ---------------------------------------------------------------------------
// Product URL
// ---------------------------------------------------------------------------

export function getProductUrlData (product, fallbackPid) {
    var directUrl =
        trimString(product && product.selectedProductUrl) ||
        trimString(product && product.url);

    if (directUrl) {
        return { url: directUrl, source: 'product' };
    }

    if (fallbackPid) {
        return {
            url: '/Product-Show?pid=' + encodeURIComponent(fallbackPid),
            source: 'fallbackPid',
        };
    }

    return { url: null, source: 'missing' };
}

// ---------------------------------------------------------------------------
// Variation attributes
// ---------------------------------------------------------------------------

export function isColorLikeAttributeId (attributeId) {
    var normalized = trimString(attributeId);
    if (!normalized) {
        return false;
    }

    var lower = normalized.toLowerCase();
    return (
        lower.indexOf('color') > -1 || /(?:^|[_\-.])col(?:$|[_\-.])/.test(lower)
    );
}

export function findVariationAttribute (product, attributeIds) {
    var variationAttributes =
        product && Array.isArray(product.variationAttributes)
            ? product.variationAttributes
            : [];
    var ids = Array.isArray(attributeIds) ? attributeIds : [];

    for (var i = 0; i < variationAttributes.length; i++) {
        var attribute = variationAttributes[i] || {};
        var attributeId = trimString(attribute.attributeId || attribute.id);
        if (!attributeId) {
            continue;
        }

        for (var j = 0; j < ids.length; j++) {
            if (attributeId.toLowerCase() === String(ids[j]).toLowerCase()) {
                return attribute;
            }
        }
    }

    return null;
}

export function findColorAttribute (product) {
    var directMatch = findVariationAttribute(product, ['color']);
    if (directMatch) {
        return directMatch;
    }

    var variationAttributes =
        product && Array.isArray(product.variationAttributes)
            ? product.variationAttributes
            : [];

    for (var i = 0; i < variationAttributes.length; i++) {
        var attribute = variationAttributes[i] || {};
        var attributeId = trimString(attribute.attributeId || attribute.id);
        if (isColorLikeAttributeId(attributeId)) {
            return attribute;
        }
    }

    return null;
}

// ---------------------------------------------------------------------------
// Size resolution
// ---------------------------------------------------------------------------

function parseTimestamp (value) {
    if (value === null || value === undefined) {
        return null;
    }

    if (value instanceof Date) {
        return isFinite(value.getTime()) ? value.getTime() : null;
    }

    if (typeof value === 'number') {
        if (!isFinite(value)) {
            return null;
        }
        // Support unix seconds as well as milliseconds.
        return value < 1000000000000 ? value * 1000 : value;
    }

    var asString = trimString(value);
    if (!asString) {
        return null;
    }

    if (/^\d+$/.test(asString)) {
        var parsedNumber = parseInt(asString, 10);
        if (isFinite(parsedNumber)) {
            return parsedNumber < 1000000000000
                ? parsedNumber * 1000
                : parsedNumber;
        }
    }

    var parsedDate = Date.parse(asString);
    return isFinite(parsedDate) ? parsedDate : null;
}

function getNumericSignal (value) {
    if (value && typeof value === 'object') {
        if (value.value !== null && value.value !== undefined) {
            value = value.value;
        } else {
            return null;
        }
    }

    var parsed = parsePriceNumber(value);
    if (parsed !== null && isFinite(parsed)) {
        return parsed;
    }

    return null;
}

function isComingSoonFlagEnabled (value) {
    return (
        value === true ||
        value === 'true' ||
        value === 1 ||
        value === '1'
    );
}

function isComingSoonActive (variationValue) {
    if (!variationValue) {
        return false;
    }

    var hasComingSoonFlag =
        isComingSoonFlagEnabled(variationValue.isComingSoon) ||
        isComingSoonFlagEnabled(variationValue.comingSoon);
    if (!hasComingSoonFlag) {
        return false;
    }

    var endDateTimestamp = parseTimestamp(
        variationValue.comingSoonEndDate ||
            variationValue.comingsoonEndDate ||
            variationValue.endDate,
    );
    if (endDateTimestamp === null) {
        return true;
    }

    return endDateTimestamp > Date.now();
}

function getAllocationSignal (variationValue) {
    if (!variationValue) {
        return null;
    }

    var directAllocation = getNumericSignal(variationValue.allocation);
    if (directAllocation !== null) {
        return directAllocation;
    }

    var ats = getNumericSignal(variationValue.ats);
    if (ats !== null) {
        return ats;
    }

    var upperAts = getNumericSignal(variationValue.ATS);
    if (upperAts !== null) {
        return upperAts;
    }

    var inventoryAts =
        variationValue.inventoryRecord && variationValue.inventoryRecord.ATS
            ? getNumericSignal(variationValue.inventoryRecord.ATS)
            : null;
    if (inventoryAts !== null) {
        return inventoryAts;
    }

    return null;
}

export function getProductSizeOptions (product, fallbackPid, inStock) {
    var explicitSizeValues = Array.isArray(product && product.sizes)
        ? product.sizes
        : [];
    var sizeAttribute = findVariationAttribute(product, ['size']);
    var sizeValues =
        sizeAttribute && Array.isArray(sizeAttribute.values)
            ? sizeAttribute.values
            : [];

    if (explicitSizeValues.length) {
        sizeValues = explicitSizeValues;
    }

    var options = [];
    var optionBySku = {};

    for (var i = 0; i < sizeValues.length; i++) {
        var sizeValue = sizeValues[i] || {};
        var rawSkuString = trimString(sizeValue.sku);
        var sku =
            (rawSkuString && rawSkuString !== '0' ? rawSkuString : null) ||
            trimString(sizeValue.sizeId) ||
            trimString(sizeValue.productId) ||
            fallbackPid;
        var name =
            trimString(sizeValue.displayValue) ||
            trimString(sizeValue.value) ||
            trimString(sizeValue.id) ||
            'Default';
        var selectable =
            sizeValue.selectable !== false && sizeValue.disabled !== true;
        var comingSoon = isComingSoonActive(sizeValue);
        var allocation = getAllocationSignal(sizeValue);
        var hasAllocation = allocation !== null;
        var sizeInStock =
            selectable &&
            !comingSoon &&
            (!hasAllocation || allocation > 0);

        var option = {
            name: name,
            sku: sku,
            allocation: hasAllocation ? allocation : null,
            inStock: sizeInStock,
            selected: sizeValue.selected === true,
        };

        if (optionBySku[sku]) {
            optionBySku[sku].selected =
                optionBySku[sku].selected || option.selected;
            optionBySku[sku].inStock =
                optionBySku[sku].inStock || option.inStock;
            continue;
        }

        optionBySku[sku] = option;
        options.push(option);
    }

    if (!options.length) {
        var fallbackInStock = inStock !== false;
        options.push({
            name: 'Default',
            sku: fallbackPid,
            inStock: fallbackInStock,
            selected: true,
        });
    }

    return options;
}

export function getPreferredCartPid (sizeOptions, fallbackPid) {
    var options = Array.isArray(sizeOptions) ? sizeOptions : [];

    for (var i = 0; i < options.length; i++) {
        if (
            options[i].selected &&
            options[i].inStock &&
            options[i].sku &&
            options[i].sku !== fallbackPid
        ) {
            return options[i].sku;
        }
    }

    for (var j = 0; j < options.length; j++) {
        if (
            options[j].inStock &&
            options[j].sku &&
            options[j].sku !== fallbackPid
        ) {
            return options[j].sku;
        }
    }

    for (var k = 0; k < options.length; k++) {
        if (options[k].sku && options[k].sku !== fallbackPid) {
            return options[k].sku;
        }
    }

    return fallbackPid;
}

// ---------------------------------------------------------------------------
// Multi-color helpers
// ---------------------------------------------------------------------------

export function getAllColorValues (product) {
    // Prefer the enriched "colors" array from the server payload
    // (LiveShopping-ProductData enriches it with isComingSoon/selectable
    // from BM variation-group custom attributes).
    if (product && Array.isArray(product.colors) && product.colors.length) {
        return product.colors;
    }

    var colorAttribute = findColorAttribute(product);
    if (!colorAttribute || !Array.isArray(colorAttribute.values)) {
        return [];
    }

    return colorAttribute.values;
}

export function isColorValueAvailable (colorValue) {
    if (!colorValue) {
        return false;
    }
    if (isComingSoonActive(colorValue)) {
        return false;
    }
    if (colorValue.isSoldOut === true) {
        return false;
    }
    if (colorValue.selectable === false) {
        return false;
    }

    var allocation = getAllocationSignal(colorValue);
    if (allocation !== null && allocation <= 0) {
        return false;
    }

    return true;
}

// ---------------------------------------------------------------------------
// Per-color product builder (single-call hydration)
// ---------------------------------------------------------------------------

/**
 * Builds a lightweight product object for a specific color variation from
 * the master product response.  Uses per-color price data from
 * variationAttributes (populated when includeVariationPrices=true) and
 * per-color images when available.
 *
 * @param {Object} masterProduct - The fullProduct from LiveShopping-ProductData
 * @param {Object} colorValue    - A color entry from getAllColorValues()
 * @returns {Object} A product-like object suitable for buildVariation()
 */
export function buildColorProduct (masterProduct, colorValue) {
    // --- Per-color images ---
    // Prefer per-color largeImages (enriched by server), then color swatch/small,
    // then master product images as ultimate fallback.
    var colorImages = null;

    // Server-enriched large images (from variation group)
    if (colorValue && Array.isArray(colorValue.largeImages) && colorValue.largeImages.length) {
        colorImages = { large: colorValue.largeImages };
    }

    // Fallback: color attribute images (swatch + small)
    if (!colorImages && colorValue && colorValue.images) {
        var attrImages = colorValue.images;
        var hasAttrLarge = Array.isArray(attrImages.large) && attrImages.large.length;
        var hasAttrSmall = Array.isArray(attrImages.small) && attrImages.small.length;
        if (hasAttrLarge || hasAttrSmall) {
            colorImages = attrImages;
        }
    }

    // Ultimate fallback: master product images
    if (!colorImages) {
        colorImages = masterProduct && masterProduct.images ? masterProduct.images : {};
    }

    // --- Per-color price ---
    // Use per-color price enriched by server (enrichColorsWithProductData),
    // fall back to master price.
    var price = masterProduct && masterProduct.price ? masterProduct.price : {};

    if (colorValue) {
        var regularPrice = colorValue.regularPrice;
        var salePrice = colorValue.salePrice || colorValue.detailPrice;
        var regularValue = parsePriceNumber(
            typeof regularPrice === 'object' && regularPrice !== null
                ? regularPrice.value
                : regularPrice
        );
        var saleValue = parsePriceNumber(
            typeof salePrice === 'object' && salePrice !== null
                ? salePrice.value
                : salePrice
        );

        if (regularValue !== null || saleValue !== null) {
            price = {};
            var currentValue = saleValue || regularValue;
            if (currentValue !== null) {
                price.sales = { value: currentValue };
                price.value = currentValue;
            }
            if (regularValue !== null) {
                price.list = { value: regularValue };
            }
        }
    }

    // --- Per-color sizes ---
    // Server enriches each color entry with a filtered sizes array.
    var sizes = null;
    if (colorValue && Array.isArray(colorValue.sizes) && colorValue.sizes.length) {
        sizes = colorValue.sizes;
    }

    var product = {
        id: masterProduct && masterProduct.id,
        productName: getProductName(masterProduct),
        brand: getProductBrand(masterProduct),
        shortDescription: masterProduct && masterProduct.shortDescription,
        description: masterProduct && masterProduct.description,
        longDescription: masterProduct && masterProduct.longDescription,
        selectedProductUrl: masterProduct && masterProduct.selectedProductUrl,
        available:
            colorValue
                ? colorValue.selectable !== false && colorValue.isSoldout !== true
                : masterProduct && masterProduct.available !== false,
        images: colorImages,
        price: price,
    };

    if (sizes) {
        product.sizes = sizes;
    }

    return product;
}
